/**
 * Theme Type Definitions
 * Centralized theme types for consistent usage across the application
 */

/**
 * Available theme options that users can select
 */
export type Theme = 'system' | 'light' | 'dark' | 'neo-brutal';

/**
 * Themes that can actually be applied to the DOM
 * System preference can only resolve to light or dark
 */
export type ResolvedTheme = 'light' | 'dark' | 'neo-brutal';

/**
 * Themes that system preference can resolve to
 * System preference detection only supports light/dark
 */
export type SystemResolvedTheme = 'light' | 'dark';

/**
 * Theme context interface for the theme provider
 */
export interface ThemeContextType {
  /** Current theme setting (including 'system') */
  theme: Theme;
  /** Actual resolved theme applied to DOM */
  resolvedTheme: ResolvedTheme;
  /** Whether the resolved theme is dark */
  isDark: boolean;
  /** Whether the current theme setting is 'system' */
  isSystem: boolean;
  /** Whether the resolved theme is neo-brutal */
  isNeoBrutal: boolean;
  /** Toggle between themes in sequence */
  toggleTheme: () => void;
  /** Set a specific theme */
  setTheme: (theme: Theme) => void;
}

/**
 * Theme configuration for components
 */
export interface ThemeConfig {
  /** Default theme to use */
  defaultTheme: Theme;
  /** Whether to store theme preference in localStorage */
  enableStorage: boolean;
  /** Storage key for theme preference */
  storageKey: string;
  /** Whether to detect system preference */
  enableSystemDetection: boolean;
}

/**
 * Theme metadata for display purposes
 */
export interface ThemeMetadata {
  /** Theme identifier */
  value: Theme;
  /** Display name for UI */
  label: string;
  /** Business-friendly label for professional users */
  businessLabel: string;
  /** Description of the theme */
  description: string;
  /** Business-focused description emphasizing user benefits */
  businessDescription: string;
  /** User benefit explanation */
  userBenefit: string;
  /** Icon component or name */
  icon: string;
  /** Whether theme supports system detection */
  supportsSystemDetection?: boolean;
  /** Whether theme should be shown in production for business users */
  showInProduction?: boolean;
}

/**
 * CSS class names for themes
 */
export const THEME_CLASSES: Record<ResolvedTheme, string> = {
  light: 'light',
  dark: 'dark',
  'neo-brutal': 'neo-brutal',
} as const;

/**
 * Default theme configuration
 */
export const DEFAULT_THEME_CONFIG: ThemeConfig = {
  defaultTheme: 'light',
  enableStorage: true,
  storageKey: 'quotable-theme',
  enableSystemDetection: true,
} as const;

/**
 * Theme metadata for UI components
 */
export const THEME_METADATA: Record<Theme, ThemeMetadata> = {
  light: {
    value: 'light',
    label: 'Light',
    businessLabel: 'Professional Light',
    description: 'Clean, professional appearance for daytime use',
    businessDescription: 'Clean, bright interface ideal for detailed work',
    userBenefit: 'Best for precision work and document review',
    icon: 'Sun',
    showInProduction: true,
  },
  dark: {
    value: 'dark',
    label: 'Dark',
    businessLabel: 'Focus Dark',
    description: 'Easy on eyes for low-light environments',
    businessDescription: 'Reduced eye strain for extended use',
    userBenefit: 'Comfortable for long sessions and low-light environments',
    icon: 'Moon',
    showInProduction: true,
  },
  'neo-brutal': {
    value: 'neo-brutal',
    label: 'Neo-Brutal',
    businessLabel: 'High Contrast',
    description: 'Bold, high-contrast design with striking aesthetics',
    businessDescription: 'Maximum clarity with enhanced visibility',
    userBenefit: 'Ideal for quick scanning and high-visibility needs',
    icon: 'Palette',
    showInProduction: false, // Hidden in production for B2B users
  },
  system: {
    value: 'system',
    label: 'Auto',
    businessLabel: 'Smart Mode',
    description: 'Follows your system preference',
    businessDescription: 'Automatically adapts to your device settings',
    userBenefit: 'Matches your device preferences automatically',
    icon: 'Sun',
    supportsSystemDetection: true,
    showInProduction: true,
  },
} as const;

/**
 * Type guard to check if a string is a valid theme
 */
export function isValidTheme(value: string): value is Theme {
  return ['system', 'light', 'dark', 'neo-brutal'].includes(value);
}

/**
 * Type guard to check if a string is a valid resolved theme
 */
export function isValidResolvedTheme(value: string): value is ResolvedTheme {
  return ['light', 'dark', 'neo-brutal'].includes(value);
}

/**
 * Get the CSS class name for a resolved theme
 */
export function getThemeClassName(theme: ResolvedTheme): string {
  return THEME_CLASSES[theme];
}

/**
 * Get theme metadata by theme value
 */
export function getThemeMetadata(theme: Theme): ThemeMetadata {
  return THEME_METADATA[theme];
}

/**
 * Resolve system theme to actual theme
 */
export function resolveSystemTheme(): SystemResolvedTheme {
  if (typeof window === 'undefined') {
    return 'light'; // Default for SSR
  }
  
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Resolve any theme to a concrete theme that can be applied
 */
export function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') {
    return resolveSystemTheme();
  }
  return theme as ResolvedTheme;
}
